import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Shop component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconShop extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.75 1.77905C1.33579 1.77905 1 2.11484 1 2.52905C1 2.94327 1.33579 3.27905 1.75 3.27905H4.93572C5.05427 3.27905 5.15651 3.36231 5.18053 3.4784L7.85058 16.3836C8.01871 17.1962 8.73444 17.7791 9.56428 17.7791H19.25C19.6642 17.7791 20 17.4433 20 17.0291C20 16.6148 19.6642 16.2791 19.25 16.2791H9.56428C9.44573 16.2791 9.34349 16.1958 9.31947 16.0797L8.94692 14.2791H20.4201C21.2622 14.2791 21.9849 13.6793 22.1401 12.8516L23.2651 6.85156C23.467 5.77458 22.6408 4.77905 21.5451 4.77905H6.9814L6.64942 3.17449C6.48129 2.36187 5.76555 1.77905 4.93572 1.77905H1.75ZM7.29175 6.27905L8.63828 12.7873C8.67473 12.7819 8.71204 12.7791 8.75 12.7791H20.4201C20.5404 12.7791 20.6436 12.6934 20.6658 12.5751L21.7908 6.57512C21.8196 6.42127 21.7016 6.27905 21.5451 6.27905H7.29175ZM11.5 21.355C11.5 22.1834 10.8284 22.855 10 22.855C9.17157 22.855 8.5 22.1834 8.5 21.355C8.5 20.5266 9.17157 19.855 10 19.855C10.8284 19.855 11.5 20.5266 11.5 21.355ZM18.5 22.855C19.3284 22.855 20 22.1834 20 21.355C20 20.5266 19.3284 19.855 18.5 19.855C17.6716 19.855 17 20.5266 17 21.355C17 22.1834 17.6716 22.855 18.5 22.855Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-shop') === undefined) {
  customElements.define('cat-icon-shop', BlocksIconShop);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-shop': BlocksIconShop;
  }
}
